import './megaMenu.scss'
import { type FC, type KeyboardEvent, type RefObject, useEffect, useRef, useState } from 'react'
import { CmsLinkComponent } from '@/cms/components/cmsLink/CmsLink'
import type { HeaderLinksPropsApi } from '../models'

interface MegaMenuProps {
    selectedLink: HeaderLinksPropsApi | null
    isOpen?: boolean
    navItemRefs: RefObject<Map<string, HTMLButtonElement | HTMLAnchorElement>>
    onClose: () => void
    setFocusNextKey: (key: string | null) => void
}

export const MegaMenu: FC<MegaMenuProps> = ({
    selectedLink,
    isOpen = false,
    navItemRefs,
    onClose,
    setFocusNextKey,
}) => {
    const [delayedLink, setDelayedLink] = useState<HeaderLinksPropsApi | null>(selectedLink)

    const menuRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (selectedLink) {
            setDelayedLink(selectedLink)
        }
    }, [selectedLink])

    useEffect(() => {
        if (!isOpen) return

        requestAnimationFrame(() => {
            menuRef.current?.querySelector<HTMLAnchorElement>('a')?.focus()
        })
    }, [isOpen, delayedLink])

    const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
        if (!menuRef.current) return

        const focusable = menuRef.current.querySelectorAll<HTMLAnchorElement>('a')
        if (!focusable.length) return

        const firstLink = focusable[0]
        const lastLink = focusable[focusable.length - 1]
        const navItems = [...navItemRefs.current.keys()]
        const currentIndex = navItems.indexOf(delayedLink?.primaryPageName ?? '')

        // shift + tab => returns to the current nav item and closes the megamenu
        if (e.key === 'Tab' && e.shiftKey && document.activeElement === firstLink) {
            e.preventDefault()
            const currentItem = navItems[currentIndex]

            onClose()

            setFocusNextKey(currentItem ?? null)
        }

        // Tab from last link => next nav item
        if (e.key === 'Tab' && !e.shiftKey && document.activeElement === lastLink) {
            e.preventDefault()
            const nextItem = navItems[currentIndex + 1]

            onClose()

            setFocusNextKey(nextItem ?? null)
        }
    }

    return (
        <div className={`mega-menu${isOpen ? ' expanded' : ''}`}>
            <div ref={menuRef} className="content container-lg-padding" onKeyDown={handleKeyDown}>
                <div className="title">{delayedLink?.primaryPageName}</div>

                <hr className="vertical-divider" />

                <div className="sublinks">
                    {delayedLink?.sublinks.map((secondaryLink, index) => (
                        <CmsLinkComponent key={index} link={secondaryLink} className="nav-link nav-sublink">
                            {secondaryLink.name}
                        </CmsLinkComponent>
                    ))}
                </div>
            </div>
        </div>
    )
}
